import { useCallback, useState } from "react";
import { useSlidePanelMounted } from "./useSlidePanelMounted";

/**
 * All-in-one hook for managing a SlidePanel's open state.
 *
 * Encapsulates three concerns that every slide-panel consumer needs:
 *  1. `isOpen`   — whether the panel is logically open (drives animations)
 *  2. `isMounted` — whether the component tree should stay in the DOM
 *                   (true while open AND during the slide-out transition)
 *  3. `open` / `close` — stable callbacks to show / hide the panel
 *
 * @example
 * const { isOpen, isMounted, open, close } = useSlidePanelState();
 *
 * return (
 *   <>
 *     <button onClick={open}>Add item</button>
 *     {isMounted && (
 *       <MyFormPanel open={isOpen} onClose={close} ... />
 *     )}
 *   </>
 * );
 */
export function useSlidePanelState(initialOpen = false) {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const isMounted = useSlidePanelMounted(isOpen);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  return { isOpen, isMounted, open, close };
}
